import React, { useEffect, useState } from 'react'
import {
  Box, Button, Checkbox, Divider, IconButton, ListItemButton, ListItemIcon, ListItemText, Popover, Stack, TextField, Typography, CircularProgress,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import {
  WishlistSummary, addLocationToWishlist, createWishlist, removeLocationFromWishlist,
} from '../lib/wishlists'

interface Props {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  locationId: number
  wishlists: WishlistSummary[]
  onWishlistsChange: (next: WishlistSummary[]) => void
  wishlisted: boolean
  onToggleWishlisted: () => Promise<void> | void
}

export default function WishlistMenu({ anchorEl, open, onClose, locationId, wishlists, onWishlistsChange, wishlisted, onToggleWishlisted }: Props) {
  const [busyGeneral, setBusyGeneral] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setCreating(false)
      setNewName('')
      setError(null)
    }
  }, [open])

  const replace = (updated: WishlistSummary) => {
    onWishlistsChange(wishlists.map((w) => (w.id === updated.id ? updated : w)))
  }

  const handleToggle = async (wishlist: WishlistSummary) => {
    if (busyId) return
    setBusyId(wishlist.id)
    setError(null)
    const isMember = wishlist.location_ids.includes(locationId)
    const result = isMember
      ? await removeLocationFromWishlist(wishlist.id, locationId)
      : await addLocationToWishlist(wishlist.id, locationId)
    if (result) replace(result)
    else setError('Could not update list')
    setBusyId(null)
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) return
    setSubmitting(true)
    setError(null)
    const created = await createWishlist(name)
    if (!created) {
      setError('Could not create list (name may be taken)')
      setSubmitting(false)
      return
    }
    const added = await addLocationToWishlist(created.id, locationId)
    const finalEntry = added ?? created
    onWishlistsChange([...wishlists, finalEntry].sort((a, b) => a.name.localeCompare(b.name)))
    setNewName('')
    setCreating(false)
    setSubmitting(false)
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { width: 280, p: 1 } } }}
    >
      <Stack direction="row" alignItems="center" sx={{ px: 1, py: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Save to list
        </Typography>
        <IconButton size="small" aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Divider />

      <ListItemButton
        onClick={async () => {
          if (busyGeneral) return
          setBusyGeneral(true)
          try { await onToggleWishlisted() } finally { setBusyGeneral(false) }
        }}
        disabled={busyGeneral}
        sx={{ borderRadius: 1, py: 0.5 }}
      >
        <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
          {busyGeneral ? <CircularProgress size={18} /> : (wishlisted ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />)}
        </ListItemIcon>
        <ListItemText
          primary="Saved"
          secondary={wishlisted ? 'In your saved list' : 'Add to your saved list'}
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'caption' }}
        />
        <Checkbox edge="end" checked={wishlisted} tabIndex={-1} disableRipple sx={{ p: 0 }} />
      </ListItemButton>

      <Divider />

      <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
        {wishlists.length === 0 && !creating && (
          <Typography variant="caption" sx={{ px: 1.5, py: 1, display: 'block', color: 'text.secondary' }}>
            No lists yet. Create one to organize your saves.
          </Typography>
        )}
        {wishlists.map((w) => {
          const checked = w.location_ids.includes(locationId)
          const busy = busyId === w.id
          return (
            <ListItemButton
              key={w.id}
              onClick={() => handleToggle(w)}
              disabled={busy}
              sx={{ borderRadius: 1, py: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {busy ? <CircularProgress size={18} /> : <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple sx={{ p: 0 }} />}
              </ListItemIcon>
              <ListItemText
                primary={w.name}
                secondary={`${w.items_count} ${w.items_count === 1 ? 'place' : 'places'}`}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItemButton>
          )
        })}
      </Box>

      <Divider />

      {creating ? (
        <Box sx={{ p: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="List name (e.g. Trip to Seoul)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreate() } }}
            inputProps={{ maxLength: 80 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" onClick={() => { setCreating(false); setNewName('') }} disabled={submitting}>Cancel</Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" variant="contained" onClick={handleCreate} disabled={submitting || !newName.trim()}>
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </Stack>
        </Box>
      ) : (
        <ListItemButton onClick={() => setCreating(true)} sx={{ borderRadius: 1, py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Create new list" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
        </ListItemButton>
      )}

      {error && (
        <Typography variant="caption" sx={{ px: 1.5, color: 'error.main', display: 'block', pb: 1 }}>
          {error}
        </Typography>
      )}
    </Popover>
  )
}
